import { useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/hooks/useApi';

/** Chapter info for ToC display */
export interface ChapterInfo {
  nodeId: string;
  chapterNumber: number;
  chapterTitle: string;
}

/** Response from GET /v1/stories/{storyId}/reading-path */
export interface ReadingPathResponse {
  storyId: string;
  /** Ordered path from root to deepest visited node */
  path: ChapterInfo[];
  /** Deepest visited nodeId (convenience field for resume) */
  currentNodeId: string | null;
  /** Total chapters in the path */
  totalChapters: number;
}

// Query keys for cache management
export const readingPathKeys = {
  all: ['reading-path'] as const,
  story: (storyId: string) => [...readingPathKeys.all, storyId] as const,
};

/**
 * Fetch the reading path for a story
 * Used for ToC display and resume functionality
 */
export function useReadingPath(storyId: string | undefined) {
  return useQuery({
    queryKey: readingPathKeys.story(storyId ?? ''),
    queryFn: async () => {
      const response = await api.get<ReadingPathResponse>(`/stories/${storyId}/reading-path`);
      return response.data;
    },
    enabled: !!storyId,
    // Stale after 30 seconds - allows refetch on navigation
    staleTime: 30000,
  });
}

/**
 * Hook to invalidate the reading path cache after navigation
 * Call this after navigating to a new node to refetch the path
 */
export function useInvalidateReadingPath() {
  const queryClient = useQueryClient();

  return (storyId: string) => {
    queryClient.invalidateQueries({
      queryKey: readingPathKeys.story(storyId),
    });
  };
}
